import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { CAPTION_MEDIUM_CLASS_NAME } from "tsi-common-react/src/utils/typography";

import type {
    ImageChooserBlock,
    MattressChartBlock as MattressChartBlockValue,
} from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

const rendition = (
    image: Immutable<ImageChooserBlock>,
    key: string,
): Immutable<ImageChooserBlock["renditions"][string]> | undefined =>
    image.renditions[key];

const imageSrc = (image: Immutable<ImageChooserBlock>, key: string): string =>
    rendition(image, key)?.url ?? image.imageData ?? image.url;

export const CocoonMattressChartBlock = (
    props: Immutable<{
        value: MattressChartBlockValue;
        page_slug: string | null;
        page_extra_classes: string | null;
    }>,
): React.JSX.Element => {
    const value = props.value;
    const image = value.image;
    const imageRendition = image ? rendition(image, "width-1000") : undefined;
    const extraClasses = value.extra_classes ?? "";

    return (
        <section
            className={concatClassNames([
                styles.mattressChart,
                "mattress-chart",
                extraClasses === "chill-bundle"
                    ? styles.mattressChartChillBundle
                    : extraClasses === "slim"
                      ? styles.mattressChartSlim
                      : undefined,
                props.page_slug,
                props.page_extra_classes,
                value.background_style?.style,
                getGlobalSpacingClasses(value.spacing),
            ])}
        >
            <div
                className={concatClassNames([
                    styles.mattressChartMain,
                    "main-container",
                    extraClasses,
                ])}
            >
                <h2 className={styles.slimTitle}>{value.title}</h2>
                <p
                    className={concatClassNames([
                        "slim__subtitle",
                        styles.slimSubtitle,
                    ])}
                >
                    {value.subtitle}
                </p>

                {image && (
                    <img
                        src={imageSrc(image, "width-1000")}
                        alt={image.default_alt_text}
                        className={concatClassNames([
                            styles.mattressCut,
                            "mattress-cut",
                            "responsive",
                        ])}
                        width={imageRendition?.width}
                        height={imageRendition?.height}
                        loading="lazy"
                        decoding="async"
                    />
                )}

                <div className={styles.featureBullets}>
                    {(value.feature_bullet_points ?? []).map(
                        (featureBullet, i) => (
                            <div
                                key={i}
                                className={concatClassNames([
                                    styles.featureBullet,
                                    `feature-bullet--${extraClasses}`,
                                ])}
                            >
                                <h5 className={CAPTION_MEDIUM_CLASS_NAME}>
                                    {featureBullet.title}
                                </h5>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: featureBullet.paragraph ?? "",
                                    }}
                                />
                            </div>
                        ),
                    )}
                </div>
            </div>
        </section>
    );
};
